import { _ as _define_property } from "@swc/helpers/_/_define_property";
import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { _ as _object_without_properties } from "@swc/helpers/_/_object_without_properties";
import React from 'react';
import mergeWith from 'lodash/mergeWith';
import classnames from 'classnames';
import { EditorState, ContentState, convertToRaw, TOOLBARS } from 'wix-rich-content-editor-common';
import { RichContentEditor, RichContentEditorModal } from 'wix-rich-content-editor';
import { getPlugins, modalsMap } from '../plugins';
import { FeatureExtractor } from '../features/Extractor';
import Entry from './Mention/Entry';
import ToolbarWrapper from './ToolbarWrapper';
import UploadDropzone from '../../UploadDropzone';
import { matchMentionsAndAttachEntities } from '../plugins/mention/modifiers';
import { InlineToolbarButtons, Plugins } from '../enums';
import baseTheme from '../theme';
import { attachImage } from '../plugins/plugin-image/modifiers';
import { addText, markEmptyAtomicAdjacentAndFocused, fixAtomicsBlockingEditing, getCurrentBlock } from '../utils';
import ExternalToolbar from './ExternalToolbar/ExternalToolbar';
import SideToolbar from './SideToolbar/SideToolbar';
import { isAndroid } from '../../utils/browser';
import { Bold, Italic, Underline, Blockquote, AlignLeft, AlignCenter, AlignRight, AlignJustify, OrderedList, UnorderedList, inactiveIconTitle, TitleOne, TitleTwo } from './icons';
import daContentNormalizer from './Viewer/daContentNormalizer';
import { MIME_CONFIG } from '../../utils/mime';
const noop = ()=>{
// noop
};
var _React_PureComponent;
class DaEditor extends (_React_PureComponent = React.PureComponent) {
    setup(props) {
        var _plugins_Plugins_DND_UPLOAD;
        const { onImageRequested, plugins, features, onModalClose, featureFlags, scrollableRef, currentUser } = props;
        // Features
        this.featureExtractor.register(features);
        // Editor API
        this.helpers = {
            featureFlags,
            handleFileSelection: (index, multiple, updateEntity, deleteBlock, componentData)=>{
                onImageRequested && onImageRequested(multiple, updateEntity, componentData);
            },
            openModal: (data)=>{
                const { modalStyles } = data, modalProps = _object_without_properties(data, [
                    "modalStyles"
                ]);
                this.onModalOpen(modalProps, modalStyles);
            },
            closeModal: ()=>{
                onModalClose && onModalClose();
            },
            onVideoSelected: ()=>{},
            scrollableRef,
            currentUser,
            fetchFreshDeviationData: this.fetchFreshDeviationData
        };
        const { plugins: pluginsCreated, extras } = getPlugins(plugins, featureFlags);
        this.plugins = pluginsCreated;
        this.extras = extras;
        this.useDropzone = !(plugins === null || plugins === void 0 ? void 0 : (_plugins_Plugins_DND_UPLOAD = plugins[Plugins.DND_UPLOAD]) === null || _plugins_Plugins_DND_UPLOAD === void 0 ? void 0 : _plugins_Plugins_DND_UPLOAD.pasteOnly);
    }
    componentDidCatch() {
        this.setState({
            initialContent: this.state.editorState && convertToRaw(this.state.editorState.getCurrentContent()),
            editorState: undefined
        });
    }
    componentDidMount() {
        this.setState({
            isMounted: true
        });
    }
    onModalOpen(modalProps, modalStyles) {
        this.props.onModalOpen && this.props.onModalOpen(()=>/*#__PURE__*/ React.createElement(RichContentEditorModal, _object_spread({
                modalsMap: modalsMap
            }, modalProps)), modalProps, modalStyles);
    }
    getEditorAPI() {
        return {
            clearEditor: this.clearState,
            focusEditor: this.editor.focus,
            blurEditor: this.editor.blur,
            getCurrentSelection: this.getCurrentSelection,
            setSelection: this.setSelection,
            moveFocusToEnd: this.moveFocusToEnd,
            /**
       * Workaround for androids compose state where we
       * lose words on submit, this waits for the onChange
       * after we blur/focus the editor.
       */ resolveEditorStateForAndroid: async ()=>{
                if (isAndroid()) {
                    const wait = (ms)=>{
                        return new Promise((resolve)=>setTimeout(resolve, ms));
                    };
                    return Promise.race([
                        wait(2000),
                        new Promise((resolve, reject)=>{
                            this.editorWaitingForUpdatePromiseResolver = (state)=>{
                                resolve(state);
                            };
                            requestAnimationFrame(()=>{
                                var _this_editor, _this_editor1;
                                (_this_editor = this.editor) === null || _this_editor === void 0 ? void 0 : _this_editor.blur();
                                (_this_editor1 = this.editor) === null || _this_editor1 === void 0 ? void 0 : _this_editor1.focus();
                            });
                        })
                    ]);
                }
                return Promise.resolve();
            }
        };
    }
    getEditorToolbars() {
        const retrievedToolbars = {};
        retrievedToolbars[TOOLBARS.INSERT_PLUGIN] = this.retrieveToolbar((props)=>/*#__PURE__*/ React.createElement(ExternalToolbar, _object_spread({
                withTooltips: this.props.showToolbarTooltips
            }, this.editorToolbarProps, props)));
        // Hack we reach into internals here to get side toolbar
        retrievedToolbars[TOOLBARS.SIDE] = this.editor.editor.toolbars[TOOLBARS.SIDE].SideToolbar;
        return retrievedToolbars;
    }
    getThemeClassName() {
        const { isDarkTheme } = this.props;
        return `da-editor-theme-${isDarkTheme ? 'dark' : 'bright'}`;
    }
    getWrapperProps() {
        const { hideSideToolbar, fixedParagraph, showMediaAlwaysSmall, isMobile, editorKey } = this.props;
        const { isLocked } = this.state;
        const wrapperProps = {};
        if (hideSideToolbar) {
            wrapperProps['data-daeditor-hide-side-toolbar'] = true;
        }
        if (fixedParagraph) {
            wrapperProps['data-daeditor-fixed-paragraph'] = true;
        }
        if (showMediaAlwaysSmall) {
            wrapperProps['data-daeditor-small-media'] = true;
        }
        if (isMobile) {
            wrapperProps['data-daeditor-hide-inline-toolbar'] = true;
        }
        if (isLocked) {
            wrapperProps['data-daeditor-locked'] = true;
        }
        wrapperProps.className = classnames('da-editor', 'da-editor-short-status-text-tools', this.getThemeClassName(), this.state.isFocused && 'da-editor-focused', this.props.className, {
            [this.theme.mobile]: isMobile
        });
        if (editorKey) {
            wrapperProps['data-editor-key'] = editorKey;
        }
        return wrapperProps;
    }
    // https://github.com/wix-incubator/rich-content/blob/master/docs/UiSettings.md
    getUiSettings() {
        return {
            linkPanel: {
                showNewTabCheckbox: false,
                showNoFollowCheckbox: false,
                showSponsoredCheckbox: false,
                placeholder: 'https://www.deviantart.com/',
                externalPopups: true
            }
        };
    }
    render() {
        const { isMounted, editorState, initialContent, isLocked } = this.state;
        if (!isMounted) {
            return null;
        }
        const { locale, localeResource, isMobile, placeholder, tabIndex, handleReturn, showMediaAlwaysSmall, editorKey, stripPastedStyles, onFileDrop, plugins: pluginsConfig, charLimit } = this.props;
        const { MentionSuggestions } = this.extras;
        const hasDndUpload = Object.keys(pluginsConfig || {}).includes(Plugins.DND_UPLOAD) && this.useDropzone;
        const localeProps = {
            locale,
            localeResource
        };
        return /*#__PURE__*/ React.createElement("div", this.getWrapperProps(), /*#__PURE__*/ React.createElement(RichContentEditor, _object_spread_props(_object_spread({
            iframeSandboxDomain: `https://06df053f-eeff-4e14-9d00-22361196feda.filesusr.com`,
            readOnly: isLocked,
            editorKey: editorKey,
            ref: this.setEditorRef,
            onChange: this.onChange,
            helpers: this.helpers,
            plugins: this.plugins,
            relValue: "nofollow ugc",
            anchorTarget: "_blank",
            initialState: initialContent,
            editorState: editorState,
            isMobile: isMobile,
            config: _object_spread_props(_object_spread({}, pluginsConfig), {
                getToolbarSettings: this.getToolbarSettings,
                image: {
                    showMediaAlwaysSmall
                },
                gallery: {
                    showMediaAlwaysSmall
                },
                uiSettings: this.getUiSettings()
            })
        }, localeProps), {
            theme: this.theme,
            placeholder: placeholder,
            tabIndex: tabIndex,
            onFocus: this.onFocus,
            onBlur: this.onBlur,
            handleReturn: handleReturn,
            handlePastedText: this.handlePastedText,
            handleBeforeInput: this.handleBeforeInput,
            stripPastedStyles: stripPastedStyles,
            maxTextLength: charLimit
        })), MentionSuggestions && /*#__PURE__*/ React.createElement(MentionSuggestions, {
            key: editorKey,
            onSearchChange: this.onMentionRequested,
            suggestions: this.state.mentions,
            entryComponent: Entry
        }), hasDndUpload && /*#__PURE__*/ React.createElement(UploadDropzone, {
            uploadFile: onFileDrop(this.extras.uploadHelpers),
            acceptedFileTypes: MIME_CONFIG.forEditorDropSubmit
        }));
    }
    constructor(props){
        super(props);
        _define_property(this, "state", {
            initialContent: this.props.initialContent ? daContentNormalizer(this.props.initialContent) : undefined,
            isMounted: false,
            mentions: [],
            isLocked: false
        });
        _define_property(this, "editor", void 0);
        _define_property(this, "editorToolbarProps", void 0);
        _define_property(this, "editorWaitingForUpdatePromiseResolver", void 0);
        _define_property(this, "theme", void 0);
        _define_property(this, "helpers", void 0);
        _define_property(this, "featureExtractor", void 0);
        _define_property(this, "plugins", void 0);
        _define_property(this, "extras", void 0);
        _define_property(this, "wasPaste", false);
        _define_property(this, "fixMentions", false);
        _define_property(this, "lastPastedUrl", void 0);
        _define_property(this, "useDropzone", void 0);
        _define_property(this, "onChange", (editorState)=>{
            let nextState = editorState;
            const { filterPaste, plugins, onContentChange } = this.props;
            // Paste handling, consumer provides function that cleans the state
            if (filterPaste && this.wasPaste) {
                this.wasPaste = false;
                nextState = filterPaste(nextState);
            }
            // Empty block UX++
            nextState = fixAtomicsBlockingEditing(nextState);
            markEmptyAtomicAdjacentAndFocused(nextState);
            let currentContent = nextState.getCurrentContent();
            // Mentions repair (fixes mentions that are not selected via dropdown)
            if (plugins && plugins['Mention'] && this.fixMentions) {
                this.fixMentions = false;
                currentContent = matchMentionsAndAttachEntities(currentContent);
                nextState = EditorState.set(nextState, {
                    currentContent
                });
            }
            const rawContentState = convertToRaw(currentContent);
            const featuresData = this.featureExtractor.extract(nextState, rawContentState);
            onContentChange && onContentChange(rawContentState, currentContent, featuresData, nextState.getSelection());
            this.setState({
                editorState: nextState
            });
            if (this.editorWaitingForUpdatePromiseResolver) {
                this.editorWaitingForUpdatePromiseResolver({
                    rawContentState,
                    editorContent: currentContent,
                    features: featuresData
                });
                this.editorWaitingForUpdatePromiseResolver = undefined;
            }
        });
        _define_property(this, "clearState", (triggerChange = false, initialText = '')=>{
            const { onContentChange } = this.props;
            const emptyContent = ContentState.createFromText(initialText);
            const rawContentState = convertToRaw(emptyContent);
            if (!this.state.editorState) {
                return;
            }
            // To avoid an issue where the cursor jumps to the beginning of the text we
            // have to do all of this juggling in order to clear the state.
            // https://github.com/facebook/draft-js/issues/410
            const nextState = EditorState.moveFocusToEnd(EditorState.push(this.state.editorState, emptyContent, 'remove-range'));
            this.setState({
                editorState: nextState
            });
            if (triggerChange && onContentChange) {
                onContentChange(rawContentState, emptyContent, this.featureExtractor.extract(nextState, rawContentState), nextState.getSelection());
            }
        });
        _define_property(this, "setEditorRef", (ref)=>{
            const { plugins } = this.props;
            const hasDndUpload = Object.keys(plugins || {}).includes(Plugins.DND_UPLOAD);
            this.editor = ref;
            if (!this.editor) {
                return;
            }
            if (hasDndUpload) {
                const editorEl = ref.editor.editorWrapper;
                editorEl && editorEl.addEventListener('paste', this.handlePastedImage, true);
            }
            this.editorToolbarProps = this.editor.getToolbarProps(TOOLBARS.INSERT_PLUGIN);
            this.props.onEditorInit && this.props.onEditorInit(this.getEditorToolbars(), this.getEditorAPI());
        });
        _define_property(this, "getCurrentSelection", ()=>{
            const { editorState } = this.state;
            return editorState && editorState.getSelection();
        });
        _define_property(this, "setSelection", (selectionState)=>{
            const { editorState } = this.state;
            if (!editorState) {
                return;
            }
            const currentSectionState = editorState.getSelection();
            const newEditorState = EditorState.forceSelection(editorState, currentSectionState.merge(selectionState));
            this.setState({
                editorState: newEditorState
            });
        });
        _define_property(this, "moveFocusToEnd", ()=>{
            const { editorState } = this.state;
            if (!editorState) {
                return;
            }
            const newEditorState = EditorState.moveFocusToEnd(editorState);
            this.setState({
                editorState: newEditorState
            });
        });
        _define_property(this, "retrieveToolbar", (Toolbar)=>(props)=>/*#__PURE__*/ React.createElement(ToolbarWrapper, null, /*#__PURE__*/ React.createElement(Toolbar, props)));
        _define_property(this, "setMentions", (mentions)=>{
            this.setState({
                mentions
            });
        });
        _define_property(this, "onMentionRequested", (value)=>{
            this.props.onMentionRequested && this.props.onMentionRequested(value, this.setMentions);
        });
        _define_property(this, "SideWrapper", (props)=>{
            var _this_editorToolbarProps_context_getEditorState, _this_editorToolbarProps_context, _this_editorToolbarProps, _getCurrentBlock;
            if (!this.editorToolbarProps) {
                return null;
            }
            // don't allow insertion when atomic elements are currently selected
            // (so don't render sidetoolbar since it's only job is inserting new elements)
            const editorState = (_this_editorToolbarProps = this.editorToolbarProps) === null || _this_editorToolbarProps === void 0 ? void 0 : (_this_editorToolbarProps_context = _this_editorToolbarProps.context) === null || _this_editorToolbarProps_context === void 0 ? void 0 : (_this_editorToolbarProps_context_getEditorState = _this_editorToolbarProps_context.getEditorState) === null || _this_editorToolbarProps_context_getEditorState === void 0 ? void 0 : _this_editorToolbarProps_context_getEditorState.call(_this_editorToolbarProps_context);
            const isWithinAtomicBlock = !!editorState && ((_getCurrentBlock = getCurrentBlock(editorState)) === null || _getCurrentBlock === void 0 ? void 0 : _getCurrentBlock.getType()) === 'atomic';
            if (isWithinAtomicBlock) {
                return null;
            }
            return /*#__PURE__*/ React.createElement(SideToolbar, _object_spread({
                toolbarProps: this.editorToolbarProps
            }, props));
        });
        _define_property(this, "getToolbarSettings", ({ pluginButtonProps })=>{
            const { hideSideToolbar, inlineToolbarButtons, inlineToolbarDecoratorFn } = this.props;
            let externalButtons = [];
            if (pluginButtonProps) {
                externalButtons = pluginButtonProps.filter(({ toolbars })=>toolbars && toolbars.includes(TOOLBARS.INSERT_PLUGIN)).map(({ name })=>name);
                // Giphy duped and I cant find why...
                externalButtons = externalButtons.filter((name, idx)=>externalButtons.indexOf(name) === idx);
            }
            return [
                {
                    name: TOOLBARS.INLINE,
                    getButtons: ()=>{
                        return {
                            desktop: inlineToolbarButtons,
                            mobile: {
                                ios: inlineToolbarButtons,
                                android: inlineToolbarButtons
                            }
                        };
                    },
                    getToolbarDecorationFn: inlineToolbarDecoratorFn
                },
                {
                    name: TOOLBARS.SIDE,
                    getVisibilityFn: ()=>({
                            desktop: hideSideToolbar ? ()=>false : ()=>true,
                            mobile: {
                                ios: ()=>true,
                                android: ()=>true
                            }
                        }),
                    getToolbarDecorationFn: ()=>({
                            desktop: ()=>this.SideWrapper
                        })
                },
                {
                    name: TOOLBARS.MOBILE,
                    shouldCreate: ()=>({
                            mobile: {
                                ios: true,
                                android: true
                            }
                        })
                },
                {
                    name: TOOLBARS.FOOTER,
                    getVisibilityFn: ()=>({
                            desktop: ()=>false,
                            mobile: {
                                ios: ()=>false,
                                android: ()=>false
                            }
                        }),
                    shouldCreate: ()=>({
                            desktop: ()=>false,
                            mobile: {
                                ios: ()=>false,
                                android: ()=>false
                            }
                        })
                },
                {
                    name: TOOLBARS.INSERT_PLUGIN,
                    getVisibilityFn: ()=>({
                            desktop: ()=>true,
                            mobile: {
                                ios: ()=>true,
                                android: ()=>true
                            }
                        }),
                    shouldCreate: ()=>({
                            desktop: ()=>true,
                            mobile: {
                                ios: ()=>true,
                                android: ()=>true
                            }
                        }),
                    getButtons: ()=>({
                            desktop: externalButtons,
                            mobile: {
                                ios: externalButtons,
                                android: externalButtons
                            }
                        })
                },
                {
                    name: TOOLBARS.TEXT,
                    getIcons: ()=>({
                            Bold,
                            Italic,
                            Underline,
                            Blockquote,
                            AlignLeft,
                            AlignCenter,
                            AlignRight,
                            AlignJustify,
                            OrderedList,
                            UnorderedList,
                            inactiveIconTitle,
                            TitleOne,
                            TitleTwo
                        })
                }
            ];
        });
        // Listen to paste events and bail out unless an image file was pasted.
        // In such case, cancel event propagation and upload the file through the same flow as dnd.
        _define_property(this, "handlePastedImage", (ev)=>{
            const { onFileDrop } = this.props;
            const { files = [] } = ev.clipboardData || {};
            const imageFile = files[0] && MIME_CONFIG.forEditorDropSubmit.includes(files[0].type) ? files[0] : null;
            if (imageFile) {
                ev.preventDefault();
                ev.stopImmediatePropagation();
                onFileDrop(this.extras.uploadHelpers)(imageFile);
            }
        });
        _define_property(this, "handlePastedText", (text, html, editorState)=>{
            // External Paste cleanup (cleans word etc and allows internal pasting to work)
            // We do not clean here because modifying the state means you have to fully
            // handle the paste so we mark paste as done and then filter in onChange
            this.wasPaste = true;
            const { deviationPasteValidator, onDeviationPasted, plugins } = this.props;
            // Deviation paste support
            if (plugins && plugins['Image'] && deviationPasteValidator && onDeviationPasted) {
                const deviationLookup = deviationPasteValidator(text);
                if (deviationLookup) {
                    onDeviationPasted(deviationLookup, (deviation)=>{
                        if (deviation) {
                            this.setState({
                                isLocked: false
                            }, ()=>{
                                this.onChange(attachImage(deviation, editorState));
                            });
                        } else {
                            this.setState({
                                isLocked: false
                            }, ()=>{
                                this.onChange(addText(text, editorState));
                            });
                        }
                    });
                    this.setState({
                        isLocked: true
                    });
                    return 'handled';
                }
            }
            return 'not-handled';
        });
        _define_property(this, "fetchFreshDeviationData", (deviations)=>{
            const { onFetchFreshDeviations } = this.props;
            this.setState({
                isLocked: true
            });
            return new Promise((resolve, reject)=>{
                onFetchFreshDeviations && onFetchFreshDeviations(deviations, (freshDeviations)=>{
                    this.setState({
                        isLocked: false
                    });
                    if (deviations) {
                        resolve(freshDeviations);
                        requestAnimationFrame(()=>{
                            var _this_editor, _this_editor1;
                            (_this_editor = this.editor) === null || _this_editor === void 0 ? void 0 : _this_editor.blur();
                            (_this_editor1 = this.editor) === null || _this_editor1 === void 0 ? void 0 : _this_editor1.focus();
                        });
                    } else {
                        reject('not found');
                    }
                });
            });
        });
        _define_property(this, "handleBeforeInput", (chars, editorState, timestamp)=>{
            if (chars === ' ') {
                this.fixMentions = true;
            }
            return 'not-handled';
        });
        _define_property(this, "onFocus", (event)=>{
            this.setState({
                isFocused: true
            });
            this.props.onFocus && this.props.onFocus(event);
        });
        _define_property(this, "onBlur", (event)=>{
            this.setState({
                isFocused: false
            });
            this.props.onBlur && this.props.onBlur(event);
        });
        this.featureExtractor = new FeatureExtractor();
        this.theme = mergeWith(_object_spread({}, baseTheme), props.theme || {}, (defaultStyleClassName, themeClassName)=>classnames(defaultStyleClassName, themeClassName));
        this.setup(props);
    }
}
_define_property(DaEditor, "defaultProps", {
    onMentionRequested: noop,
    onImageRequested: noop,
    onEmotesRequested: noop,
    onEditorInit: noop,
    onModalOpen: noop,
    onModalClose: noop,
    locale: 'en',
    isDarkTheme: false,
    hideSideToolbar: false,
    fixedParagraph: false,
    showMediaAlwaysSmall: false,
    showToolbarTooltips: true,
    features: [],
    inlineToolbarButtons: [
        InlineToolbarButtons.BOLD,
        InlineToolbarButtons.ITALIC,
        InlineToolbarButtons.UNDERLINE,
        InlineToolbarButtons.TEXT_COLOR,
        InlineToolbarButtons.LINK,
        InlineToolbarButtons.SEPARATOR,
        InlineToolbarButtons.TITLE,
        InlineToolbarButtons.ORDERED_LIST,
        InlineToolbarButtons.UNORDERED_LIST,
        InlineToolbarButtons.BLOCKQUOTE
    ]
});
export default DaEditor;
